import { useState } from 'react';
import { misSolicitudesAPI, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import { Stamp, Loader2, CheckCircle2, XCircle, Ban } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Dar o negar una autorización sobre un caso.
 *
 * **Lo que la pantalla tiene que decir antes que los botones, y es lo más importante de esta
 * entrega:** esto **no frena el caso**. Se registra la decisión y se avisa; qué hacer con ella
 * la decide quien tiene el caso. Si la pantalla dejara creer que bloquea, alguien va a confiar
 * en un bloqueo que no existe — y eso es peor que no tener la función.
 *
 * **El comentario es opcional**, también al rechazar (decisión de Diego, 2026-09-01). Pero en el
 * rechazo se pide con el campo a la vista y no escondido detrás de un «agregar comentario»: sin
 * motivo, el próximo que mire no sabe si hay que insistir, corregir algo o dejarlo.
 *
 * **Y un rechazo cancela las otras autorizaciones que estaban esperando.** Se avisa antes de
 * apretar, porque es una consecuencia sobre el trabajo de otras personas.
 */
const ESTILO = {
  aprobada: { icono: CheckCircle2, clase: 'bg-green-50 text-green-800 border-green-200', texto: 'Aprobada' },
  rechazada: { icono: XCircle, clase: 'bg-red-50 text-red-800 border-red-200', texto: 'Rechazada' },
  cancelada: { icono: Ban, clase: 'bg-zinc-50 text-zinc-600 border-zinc-200', texto: 'Cancelada' },
};

export default function Autorizacion({ tarea, tenantId, pendientesEnElCaso = 0, onCambio }) {
  const [ocupado, setOcupado] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [confirmandoRechazo, setConfirmandoRechazo] = useState(false);

  const datos = tarea?.autorizacion || {};
  const resuelta = Boolean(datos.resultado);
  const otrasQueSeCancelan = Math.max(0, pendientesEnElCaso - 1);

  const decidir = async (aprueba) => {
    setOcupado(true);
    try {
      const llamada = aprueba ? misSolicitudesAPI.aprobarAutorizacion : misSolicitudesAPI.rechazarAutorizacion;
      const { data } = await llamada(tenantId, tarea.task_id, motivo.trim());
      const canceladas = (data?.canceladas || []).length;
      toast.success(
        aprueba
          ? 'Autorización dada'
          : canceladas
            ? `Rechazada. Se cancelaron ${canceladas} autorización(es) que esperaban.`
            : 'Rechazada',
      );
      setConfirmandoRechazo(false);
      onCambio?.();
    } catch (err) {
      toast.error(formatApiError(err, 'No se pudo registrar la decisión'));
    } finally {
      setOcupado(false);
    }
  };

  if (resuelta) {
    const e = ESTILO[datos.resultado] || ESTILO.cancelada;
    const Icono = e.icono;
    return (
      <div className={`border rounded-sm p-3 ${e.clase}`}>
        <div className="flex items-center gap-2">
          <Icono className="w-4 h-4" />
          <span className="text-sm font-medium">{e.texto}</span>
          {datos.resuelta_por && (
            <span className="text-xs opacity-80">por {datos.resuelta_por}</span>
          )}
          {datos.resuelta_at && (
            <span className="text-[10px] opacity-60">{String(datos.resuelta_at).slice(0, 10)}</span>
          )}
        </div>
        {datos.motivo && <p className="text-xs mt-1.5 whitespace-pre-wrap">{datos.motivo}</p>}
        {datos.resultado === 'cancelada' && (
          <p className="text-[11px] mt-1.5">
            Otra autorización de este caso fue rechazada, así que ésta ya no hace falta.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="border border-amber-200 bg-amber-50 rounded-sm p-3" data-testid="panel-autorizacion">
      <div className="flex items-center gap-2 mb-1.5">
        <Stamp className="w-4 h-4 text-amber-700" />
        <span className="text-sm font-medium text-amber-900">Esperan tu autorización</span>
        <Badge variant="outline" className="text-[9px] rounded-sm">pendiente</Badge>
      </div>

      {/* La frase que evita que alguien confíe en un bloqueo que no existe. */}
      <p className="text-[11px] text-amber-800 mb-2">
        Tu respuesta queda registrada y se avisa a quien corresponda. <b>No frena el caso</b>:
        qué hacer con ella lo decide quien lo tiene a cargo.
      </p>

      <label className="text-[10px] uppercase tracking-wide text-zinc-600">
        Comentario <span className="normal-case text-zinc-400">(opcional)</span>
      </label>
      <Textarea
        rows={2}
        className="mt-1 text-xs rounded-sm bg-white"
        placeholder="Si rechazás, decir por qué le ahorra una vuelta al que lo pidió."
        value={motivo}
        data-testid="motivo-autorizacion"
        onChange={(e) => setMotivo(e.target.value)}
      />

      {otrasQueSeCancelan > 0 && (
        <p className="text-[11px] text-amber-800 mt-2">
          Ojo: si rechazás, se cancelan <b>{otrasQueSeCancelan}</b> autorización(es) más que
          están esperando en este caso.
        </p>
      )}

      <div className="flex justify-end gap-2 mt-2">
        {/* **Rechazar es irreversible y se lleva las hermanas.** El aviso en prosa de arriba ya
            existia y es bueno; lo que faltaba era el paso intermedio: el boton estaba al lado de
            "Autorizar", mismo tamaño, y un clic registraba el rechazo **y cancelaba las demas
            autorizaciones pendientes del caso**. Autorizar no lleva dialogo a proposito: es la
            decision que no cancela nada de otros. */}
        <Button
          size="sm" variant="outline"
          className="h-8 rounded-sm text-xs border-red-300 text-red-700 hover:bg-red-50"
          disabled={ocupado}
          data-testid="rechazar-autorizacion"
          onClick={() => setConfirmandoRechazo(true)}
        >
          {ocupado ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <XCircle className="w-3 h-3 mr-1" />}
          Rechazar
        </Button>
        <Button
          size="sm" className="h-8 rounded-sm text-xs"
          disabled={ocupado}
          data-testid="aprobar-autorizacion"
          onClick={() => decidir(true)}
        >
          {ocupado ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
          Autorizar
        </Button>
      </div>

      <ConfirmActionDialog
        open={confirmandoRechazo}
        onOpenChange={setConfirmandoRechazo}
        title="Rechazar la autorización"
        description={
          'Queda registrado y se le avisa a quien pidió el caso.'
          + (otrasQueSeCancelan > 0
            ? ` Además se cancelan ${otrasQueSeCancelan} autorización(es) más que están esperando en este caso.`
            : '')
          + ' No se puede deshacer.'
        }
        confirmText="Rechazar"
        onConfirm={() => decidir(false)}
      >
        {!motivo.trim() && (
          <p className="text-[11px] text-amber-800">
            No escribiste un motivo. Se puede rechazar sin él, pero quien pidió el caso se queda sin
            saber qué le falta — y vuelve a preguntar.
          </p>
        )}
      </ConfirmActionDialog>
    </div>
  );
}
